import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';
import { useNavigation, type CompositeNavigationProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FlatList,
  InteractionManager,
  Linking,
  Platform,
  RefreshControl,
  StyleSheet,
  TouchableOpacity,
  type ListRenderItemInfo,
} from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Stack } from '@/components/layout';
import { AppHeader } from '@/components/layout/app-header';
import { showAlert, SvgIcon, Text } from '@/components/ui';
import { FLOATING_TAB_BAR_CLEARANCE_TOKEN } from '@/components/ui/tabbar';
import { STRINGS } from '@/constants/strings';
import {
  CoachNotificationEmptyState,
  CoachNotificationItem,
  CoachNotificationSettingsSheet,
} from '@/features/coach/components/notifications';
import { useCoachNotifications } from '@/features/coach/hooks/use-coach-notifications';
import type { CoachNotification } from '@/features/coach/notifications/types';
import { resolveCoachNotificationRoute } from '@/features/coach/utils/resolve-coach-notification-route';
import { NotificationPermissionBanner } from '@/features/guardian-profile/components/notification-preferences/notification-permission-banner';
import { createStyles, useNotificationPermission, useScaling } from '@/hooks';
import type { AuthStackParamList, HomeTabNavigatorParamList } from '@/navigations/types';
import { colors } from '@/theme/colors';

const strings = STRINGS.coach.notifications;

type CoachNotificationsNavigation = CompositeNavigationProp<
  BottomTabNavigationProp<HomeTabNavigatorParamList, 'Notifications'>,
  NativeStackNavigationProp<AuthStackParamList>
>;

/**
 * Coach Notification Center. Reverse-chronological list (BR-01) with swipe-to-delete (BR-02),
 * a permission banner when the OS permission is denied (BR-03), and a settings bottom sheet.
 */
export function CoachNotificationsScreen() {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<CoachNotificationsNavigation>();
  const {
    isDenied,
    isUndetermined,
    canAsk: canAskNotificationPermission,
    isSupported: isNotificationSupported,
    refresh: refreshNotificationPermission,
    request: requestNotificationPermission,
  } = useNotificationPermission();
  const requestedPermissionRef = useRef(false);
  const isWeb = Platform.OS === 'web';

  // Same rule as the other notification screens: native shows the banner once the OS permission
  // is actually denied; web also covers "not asked yet" since there is no Settings deep link.
  const showPermissionBanner = isWeb
    ? isNotificationSupported && (isUndetermined || isDenied)
    : isDenied;

  // Ask once on entry — without this the status stays `undetermined` and the banner never shows.
  useEffect(() => {
    if (isWeb) return undefined;

    let cancelled = false;
    const task = InteractionManager.runAfterInteractions(() => {
      if (requestedPermissionRef.current) return;
      requestedPermissionRef.current = true;

      refreshNotificationPermission().then(status => {
        if (!cancelled && status === 'undetermined') requestNotificationPermission();
      });
    });

    return () => {
      cancelled = true;
      task.cancel();
    };
  }, [isWeb, refreshNotificationPermission, requestNotificationPermission]);

  const handlePermissionPress = useCallback(() => {
    if (!isWeb) {
      Linking.openSettings();
      return;
    }
    if (canAskNotificationPermission) {
      requestNotificationPermission();
      return;
    }
    showAlert({
      title: STRINGS.common.noticeTitle,
      message: STRINGS.common.webNotificationsDeniedMessage,
      buttons: [{ text: STRINGS.common.buttons.dismiss }],
    });
  }, [canAskNotificationPermission, isWeb, requestNotificationPermission]);
  const [showSettings, setShowSettings] = useState(false);

  const headerPaddingTop = insets.top + scale({ phone: 12, tablet: 21 });

  const handleNavigate = useCallback(
    (item: CoachNotification) => {
      const route = resolveCoachNotificationRoute(item);
      // Unmapped types (PP-562 pending) stay on the list — already marked read by the caller.
      if (!route) return;

      if (route.screen === 'Dashboard') {
        navigation.navigate('Home', { screen: 'Dashboard' });
        return;
      }
      navigation.navigate(route.tab, { screen: route.screen, params: route.params });
    },
    [navigation],
  );

  const {
    notifications,
    isRefreshing,
    handleNotificationPress,
    handleMarkAllRead,
    handleDelete,
    handleRefresh,
    handleLoadMore,
  } = useCoachNotifications({ onNavigate: handleNavigate });

  const handleBack = useCallback(() => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }
    // Opened directly from the tab bar — there is no history, so fall back to the dashboard.
    navigation.navigate('Home', { screen: 'Dashboard' });
  }, [navigation]);

  const handlePreferences = useCallback(() => {
    setShowSettings(false);
    navigation.navigate('CoachNotificationPreferences');
  }, [navigation]);

  const handleMarkAllReadPress = useCallback(() => {
    setShowSettings(false);
    handleMarkAllRead();
  }, [handleMarkAllRead]);

  const renderItem = useCallback(
    ({ item }: ListRenderItemInfo<CoachNotification>) => (
      <CoachNotificationItem
        item={item}
        onPress={handleNotificationPress}
        onDelete={handleDelete}
      />
    ),
    [handleDelete, handleNotificationPress],
  );

  const keyExtractor = useCallback((item: CoachNotification) => item.id, []);

  return (
    <GestureHandlerRootView style={styles.root}>
      <Stack flex={1} backgroundColor={colors['1']}>
        <AppHeader
          paddingTop={headerPaddingTop}
          paddingBottom={scale({ phone: 15, tablet: 21 })}
          backgroundColor={colors['0']}
          style={styles.header}>
          <AppHeader.Left>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={strings.back}
              style={styles.headerButton}
              onPress={handleBack}>
              <Stack direction="row" align="center" gap={scale(2)}>
                <SvgIcon name="IcBackBlue" size={scale(24)} color={colors.Blue} />
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Medium 14px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.headerActionText}>
                  {strings.back}
                </Text>
              </Stack>
            </TouchableOpacity>
          </AppHeader.Left>
          <AppHeader.Title>
            <Text
              variant={
                isTablet
                  ? 'Font Inter/Subtitle/Bold 14px'
                  : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
              }
              fontWeight={isTablet ? undefined : '800'}
              style={styles.headerTitle}>
              {strings.title}
            </Text>
          </AppHeader.Title>
          <AppHeader.Right>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={strings.accessibility.settings}
              style={styles.headerButton}
              onPress={() => setShowSettings(true)}>
              <SvgIcon name="IcMoreVertical" size={scale(24)} color={colors.Blue} />
            </TouchableOpacity>
          </AppHeader.Right>
        </AppHeader>

        <Stack
          flex={1}
          width="100%"
          style={[styles.body, isTablet ? styles.bodyConstrained : null]}>
          {/* BR-03 — only while the OS permission is denied */}
          {showPermissionBanner ? (
            <NotificationPermissionBanner
              message={strings.permissionWarning}
              onPress={handlePermissionPress}
              style={styles.permissionBanner}
              contentStyle={styles.permissionBannerContent}
            />
          ) : null}

          <FlatList
            data={notifications}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            contentContainerStyle={[
              styles.listContent,
              { paddingBottom: insets.bottom + scale(FLOATING_TAB_BAR_CLEARANCE_TOKEN) },
              notifications.length === 0 ? styles.listContentEmpty : null,
            ]}
            showsVerticalScrollIndicator={false}
            refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} />}
            onEndReachedThreshold={0.4}
            onEndReached={handleLoadMore}
            ListEmptyComponent={<CoachNotificationEmptyState onSettings={handlePreferences} />}
          />
        </Stack>

        <CoachNotificationSettingsSheet
          open={showSettings}
          onOpenChange={setShowSettings}
          onPreferences={handlePreferences}
          onMarkAllRead={handleMarkAllReadPress}
        />
      </Stack>
    </GestureHandlerRootView>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: { flex: 1 },
  header: {
    // Figma asks for 0.2px, but sub-hairline widths round away to nothing on device.
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors['10'],
  },
  headerButton: { paddingHorizontal: scale(4) },
  headerActionText: { color: colors.Blue, textTransform: 'uppercase' },
  headerTitle: { color: colors.Blue, textTransform: 'uppercase' },
  body: {
    alignSelf: 'center',
    paddingTop: scale(16),
  },
  bodyConstrained: {
    maxWidth: scale(380),
  },
  permissionBanner: {
    marginHorizontal: scale(16),
    marginBottom: scale(12),
  },
  permissionBannerContent: {
    borderRadius: scale(2),
  },
  listContent: { paddingHorizontal: scale(16), gap: scale(12) },
  listContentEmpty: { flexGrow: 1 },
  pressed: { opacity: 0.68 },
}));

export default CoachNotificationsScreen;
